import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Alert
} from '@mui/material';
import apiService from '../components/ReasuableFuncations/api';

const BudgetDashboard = () => {
  const [budgets, setBudgets] = useState([]);
  const [error, setError] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(false);

  const [selectedBudgetId, setSelectedBudgetId] = useState('');
  const [expectedBudget, setExpectedBudget] = useState('');
  const [actualBudget, setActualBudget] = useState('');
  const [comment, setComment] = useState('');
  const [budgetDate, setBudgetDate] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      setUserRole({ selectedOption, regionalNetworkOption, memberStateOption, countryOption });
    }
  }, []);

  const canEditDelete = (budget) => {
    if (!userRole) return false;
    
    switch (userRole.selectedOption) {
      case 'Comesa':
        return true; // Comesa users can edit/delete all budgets
      case 'Regional Network':
        return budget.regionalNetworkOption === userRole.regionalNetworkOption;
      case 'Member State':
        return budget.countryOption === userRole.countryOption;
      default:
        return false;
    }
  };

  const fetchBudgets = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData) {
        throw new Error('User data not found');
      }

      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      let response;

      switch (selectedOption) {
        case 'Comesa':
          response = await apiService.get('/api/budgets');
          break;
        case 'Regional Network':
          if (regionalNetworkOption) {
            response = await apiService.get(`/api/budgets?regionalNetwork=${regionalNetworkOption}`);
          }
          break;
        case 'Member State':
          if (memberStateOption) {
            response = await apiService.get(`/api/budgets?country=${countryOption}`);
          }
          break;
        default:
          throw new Error('Invalid selection');
      }

      if (response.statusText !== 'OK') {
        throw new Error('Failed to fetch budgets');
      }
      setBudgets(response.data);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      setError('Failed to fetch budgets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const budgetData = { expectedBudget, actualBudget, comment, budgetDate };

    try {
      const response = isEditing
        ? await apiService.put(`/api/budgets/${selectedBudgetId}`, budgetData)
        : await apiService.post(`/api/budgets`, budgetData);

      if (response.statusText !== 'OK') throw new Error('Failed to submit budget data');

      const responseData = response.data;
      setSnackbarMessage(isEditing ? 'Budget data updated successfully!' : 'Budget data submitted successfully!');
      setSnackbarSeverity('success');

      if (isEditing) {
        setBudgets((prev) =>
          prev.map((budget) => (budget._id === selectedBudgetId ? responseData : budget))
        );
      } else {
        setBudgets((prev) => [...prev, responseData]);
      }
    } catch (error) {
      console.error('Error submitting budget:', error);
      setSnackbarMessage('Error submitting budget. Please try again later.');
      setSnackbarSeverity('error');
    } finally {
      setLoading(false);
      setOpenDialog(false);
      setIsEditing(false);
      setProjectName('');
    }
  };

  const handleOpenDialog = (budget) => {
    setSelectedBudgetId(budget._id);
    setExpectedBudget(budget.expectedBudget);
    setActualBudget(budget.actualBudget);
    setComment(budget.comment);
    setBudgetDate(budget.budgetDate.split('T')[0]);
    setProjectName(budget.project ? budget.project.projectName : 'N/A');
    setOpenDialog(true);
    setIsEditing(true);
  };

  const handleDelete = async (budgetId) => {
    setLoading(true);
    try {
      const response = await apiService.delete(`/api/budgets/${budgetId}`);
      if (response.statusText !== 'OK') throw new Error('Failed to delete budget data');

      setBudgets((prev) => prev.filter((budget) => budget._id !== budgetId));
      setSnackbarMessage('Budget data deleted successfully!');
      setSnackbarSeverity('success');
    } catch (error) {
      console.error('Error deleting budget:', error);
      setSnackbarMessage('Error deleting budget. Please try again later.');
      setSnackbarSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: 'white', margin: 'auto', p: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Project Budget Dashboard
      </Typography>
      <Divider sx={{ marginBottom: 2 }} />

      {error && (
        <Typography variant="body1" color="error" align="center">
          {error}
        </Typography>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project Name</TableCell>
                <TableCell>Expected Budget</TableCell>
                <TableCell>Actual Budget</TableCell>
                <TableCell>Budget Date</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {budgets.map((budget) => (
                <TableRow key={budget._id}>
                  <TableCell>{budget.project?.projectName || 'N/A'}</TableCell>
                  <TableCell>{budget.expectedBudget}</TableCell>
                  <TableCell>{budget.actualBudget}</TableCell>
                  <TableCell>{new Date(budget.budgetDate).toLocaleDateString()}</TableCell>
                  <TableCell>{budget.comment}</TableCell>
                  <TableCell>
                    {canEditDelete(budget) && (
                      <>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(budget)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDelete(budget._id)}
                          sx={{ marginLeft: 1 }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Snackbar
          open={Boolean(snackbarMessage)}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage('')}
        >
          <Alert onClose={() => setSnackbarMessage('')} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {loading && <CircularProgress />}

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>{isEditing ? 'Edit Budget' : 'Submit Budget'}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Project Name"
              type="text"
              fullWidth
              variant="outlined"
              value={projectName}
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Expected Budget"
              type="number"
              fullWidth
              variant="outlined"
              value={expectedBudget}
              onChange={(e) => setExpectedBudget(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Actual Budget"
              type="number"
              fullWidth
              variant="outlined"
              value={actualBudget}
              onChange={(e) => setActualBudget(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Comment"
              type="text"
              fullWidth
              variant="outlined"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Budget Date"
              type="date"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={budgetDate}
              onChange={(e) => setBudgetDate(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} color="primary">
              {isEditing ? 'Update' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default BudgetDashboard;

